import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface AuthSession {
  type: 'tenant' | 'platform'
  token: string
  tenantId?: string
  adminId: number
  email: string
  role: string
}

interface TenantConfig {
  id: string
  name: string
  slug: string
  logoUrl: string | null
  logoText: string
  primaryColor: string
  tagline: string
  contactEmail: string
  currency: string
  stripeOnboarded: boolean
  stripePublishableKey?: string
}

interface AuthContextType {
  session: AuthSession | null
  tenant: TenantConfig | null
  loading: boolean
  login: (type: 'tenant' | 'platform', email: string, password: string) => Promise<void>
  logout: () => void
  loadTenantConfig: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

const STORAGE_KEY = 'camp_cloud_session'
const TENANT_KEY = 'camp_cloud_tenant'

// Get tenant slug from current URL path (e.g., /t/demo/admin -> demo)
function getTenantSlugFromPath(): string | null {
  const match = window.location.pathname.match(/^\/t\/([^\/]+)/)
  return match ? match[1] : null
}

// Get API base path for tenant routes
function getTenantApiBase(): string {
  const slug = getTenantSlugFromPath()
  return slug ? `/t/${slug}` : ''
}

// Check if we're on a platform route (no tenant context needed)
function isPlatformRoute(): boolean {
  return window.location.pathname.startsWith('/platform') || 
         window.location.pathname === '/home' ||
         window.location.pathname === '/'
}

// Check if we're on a tenant route
function isTenantRoute(): boolean {
  // Path-based tenant routes
  if (getTenantSlugFromPath()) return true
  // On root but not platform routes - could be tenant page
  if (!isPlatformRoute()) return true
  return false
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null)
  const [tenant, setTenant] = useState<TenantConfig | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Load session from storage
    const storedSession = localStorage.getItem(STORAGE_KEY)
    if (storedSession) {
      setSession(JSON.parse(storedSession))
    }
    
    // Only load tenant config if on a tenant route
    if (isTenantRoute()) {
      loadTenantConfig()
    } else {
      // On platform routes, just finish loading
      setLoading(false)
    }
  }, [])

  const loadTenantConfig = async () => {
    try {
      const base = getTenantApiBase()
      const response = await fetch(`${base}/api/config`)
      if (response.ok) {
        const data = await response.json() as { success: boolean; tenant?: TenantConfig }
        if (data.success && data.tenant) {
          setTenant(data.tenant)
          localStorage.setItem(TENANT_KEY, JSON.stringify(data.tenant))
          
          // Apply tenant branding
          document.documentElement.style.setProperty('--brand-primary', data.tenant.primaryColor)
          document.title = data.tenant.name
        }
      }
    } catch {
      // Might be on platform domain, not tenant domain
      const stored = localStorage.getItem(TENANT_KEY)
      if (stored) {
        setTenant(JSON.parse(stored))
      }
    } finally {
      setLoading(false)
    }
  }

  const login = async (type: 'tenant' | 'platform', email: string, password: string) => {
    const base = type === 'tenant' ? getTenantApiBase() : ''
    const endpoint = type === 'platform' ? '/platform/auth' : `${base}/api/auth`
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    
    const data = await response.json() as { success: boolean; error?: string; token: string; tenant?: { id: string }; admin: { id: number; email: string; role: string } }
    
    if (!response.ok) {
      throw new Error(data.error || 'Login failed')
    }
    
    const newSession: AuthSession = {
      type,
      token: data.token,
      tenantId: data.tenant?.id,
      adminId: data.admin.id,
      email: data.admin.email,
      role: data.admin.role,
    }
    
    setSession(newSession)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newSession))
  }

  const logout = () => {
    setSession(null)
    localStorage.removeItem(STORAGE_KEY)
  }

  return (
    <AuthContext.Provider value={{ session, tenant, loading, login, logout, loadTenantConfig }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export function useRequireAuth(type?: 'tenant' | 'platform') {
  const { session, loading } = useAuth()
  
  if (loading) return { session: null, loading: true, authorized: false }
  if (!session) return { session: null, loading: false, authorized: false }
  if (type && session.type !== type) return { session, loading: false, authorized: false }
  
  return { session, loading: false, authorized: true }
}

export function getAuthHeader(): HeadersInit {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (!stored) return {}
  
  const session: AuthSession = JSON.parse(stored)
  return { 'Authorization': `Bearer ${session.token}` }
}

